import { Pause, Play, RotateCw } from 'lucide-preact';
import { routeStore } from '../state/routeStore';
import {
  pausePlayback,
  playbackProgress,
  playbackState,
  playJourney,
  resumePlayback,
} from '../state/playback';
import { activeWaypoint, formatWaypointClock } from '../lib/activeWaypoint';
import { t } from '../i18n';

/**
 * Replay-the-journey toggle, shown in the drawer once the route is
 * finished. Lives here (not in the header card) so the arrival hero stays
 * focused on the "they made it" message and the action sits beside the
 * journey stats it animates.
 */
export function ReplayButton() {
  const route = routeStore.value;
  const state = playbackState.value;
  const progress = playbackProgress.value;
  if (!route?.finished) return null;
  if ((route.waypoints?.length ?? 0) <= 1) return null;

  const onClick = () => {
    if (state === 'playing') pausePlayback();
    else if (state === 'paused') resumePlayback();
    else playJourney();
  };

  // Paused-state label includes the clock time of the waypoint the head
  // froze on, so the watcher can read "Paused at 17:55" instead of a
  // context-free "Resume".
  let label: string;
  let Icon: typeof Play;
  if (state === 'playing') {
    Icon = Pause;
    label = t('playback.pause');
  } else if (state === 'paused') {
    Icon = Play;
    const time = formatWaypointClock(activeWaypoint(route, progress)?.time);
    label = time ? t('playback.pausedAt', { time }) : t('playback.resume');
  } else if (state === 'completed') {
    Icon = RotateCw;
    label = t('playback.replay');
  } else {
    Icon = Play;
    label = t('playback.play');
  }

  return (
    <button
      type="button"
      onClick={onClick}
      class="flex w-full items-center justify-center gap-2 rounded-xl bg-secondary/20 px-3 py-2.5 text-sm font-semibold text-primary hover:bg-secondary/30 transition-colors"
    >
      <Icon size={16} aria-hidden="true" />
      {label}
    </button>
  );
}
